import { Helmet } from "react-helmet-async";
import { BlogPost } from "@/utils/blogUtils";

interface BlogPostSEOProps {
  post: BlogPost;
  currentUrl: string;
}

const BlogPostSEO = ({ post, currentUrl }: BlogPostSEOProps) => {
  // Format date for schema.org
  const formattedDate = new Date(post.publish_date).toISOString();
  
  // Create a more SEO-optimized title
  const optimizedTitle = `${post.title} | Perícia Judicial Campinas`;

  return (
    <Helmet>
      <title>{optimizedTitle}</title>
      <meta name="description" content={post.meta_description} />
      <meta name="author" content={post.author} />
      <meta name="keywords" content={post.tags.join(', ') + ', perícia judicial, assistência técnica, engenharia civil, Campinas'} />
      
      {/* Open Graph tags for social sharing */}
      <meta property="og:title" content={optimizedTitle} />
      <meta property="og:description" content={post.meta_description} />
      <meta property="og:image" content={post.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content="Perícia Técnica Eng. Moacyr Moura" />
      <meta property="article:published_time" content={formattedDate} />
      <meta property="article:author" content={post.author} />
      <meta property="article:section" content="Perícia Judicial" />
      {post.tags.map((tag, index) => (
        <meta key={index} property="article:tag" content={tag} />
      ))}
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={optimizedTitle} />
      <meta name="twitter:description" content={post.meta_description} />
      <meta name="twitter:image" content={post.image} />
      <meta name="twitter:site" content="@periciatecnica" />
      <meta name="twitter:creator" content="@periciatecnica" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Additional SEO metadata */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="language" content="pt-BR" />
      <meta name="revisit-after" content="7 days" />
      <meta name="news_keywords" content={post.tags.join(', ')} />
      
      {/* Article Schema for Google Rich Snippets */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "${post.title}",
            "name": "${post.title}",
            "image": [
              "${post.image}"
            ],
            "author": {
              "@type": "Person",
              "name": "${post.author}",
              "url": "https://www.periciatecnica.eng.br/sobre"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Perícia Técnica Eng. Moacyr Moura",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.periciatecnica.eng.br/assets/images/logo.png",
                "width": 600,
                "height": 60
              }
            },
            "datePublished": "${formattedDate}",
            "dateModified": "${formattedDate}",
            "description": "${post.meta_description}",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "${currentUrl}"
            },
            "keywords": "${post.tags.join(', ')}",
            "articleSection": "Perícia Judicial",
            "wordCount": "${post.content.join(' ').split(' ').length}",
            "speakable": {
              "@type": "SpeakableSpecification",
              "cssSelector": ["h1", "article"]
            },
            "isAccessibleForFree": "True"
          }
        `}
      </script>
      
      {/* BreadcrumbList Schema */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.periciatecnica.eng.br/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://www.periciatecnica.eng.br/blog"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "${post.title}",
                "item": "${currentUrl}"
              }
            ]
          }
        `}
      </script>
    </Helmet>
  );
};

export default BlogPostSEO;
